import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Row,
  Col,
  Divider,
  message,
  Space,
  Tag,
  Alert
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  SettingOutlined,
  DollarOutlined,
  RobotOutlined,
  MailOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { adminService, SystemConfig } from '../../../services/adminService';

const SystemConfigComponent: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<SystemConfig | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await adminService.getSystemConfig();
      if (response.success && response.data) {
        const configData = response.data.config;
        setConfig(configData);
        form.setFieldsValue({
          // 停車場設置
          parkingLotName: configData.parkingLot.name,
          totalSpots: configData.parkingLot.totalSpots,
          floors: configData.parkingLot.floors,
          zones: configData.parkingLot.zones.join(', '),
          
          // 價格設置
          hourlyRate: configData.pricing.hourlyRate,
          dailyRate: configData.pricing.dailyRate,
          currency: configData.pricing.currency,
          
          // 功能設置
          aiRecognition: configData.features.aiRecognition,
          jetsonThor: configData.features.jetsonThor,
          gemmaModel: configData.features.gemmaModel,
          
          // 通知設置
          emailNotifications: configData.notifications.email,
          smsNotifications: configData.notifications.sms,
        });
      }
    } catch (error) {
      console.error('載入系統配置失敗:', error);
      message.error('載入系統配置失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const updatedConfig: SystemConfig = {
        parkingLot: {
          name: values.parkingLotName,
          totalSpots: values.totalSpots,
          floors: values.floors,
          zones: values.zones.split(',').map((zone: string) => zone.trim()).filter(Boolean),
        },
        pricing: {
          hourlyRate: values.hourlyRate,
          dailyRate: values.dailyRate,
          currency: values.currency,
        },
        features: {
          aiRecognition: values.aiRecognition,
          jetsonThor: values.jetsonThor,
          gemmaModel: values.gemmaModel,
        },
        notifications: {
          email: values.emailNotifications,
          sms: values.smsNotifications,
        },
      };

      const response = await adminService.updateSystemConfig(updatedConfig);
      if (response.success) {
        message.success('系統配置保存成功');
        setConfig(updatedConfig);
      } else {
        message.error(response.message || '保存失敗');
      }
    } catch (error) {
      console.error('保存系統配置失敗:', error);
      message.error('保存系統配置失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (config) {
      form.setFieldsValue({
        parkingLotName: config.parkingLot.name,
        totalSpots: config.parkingLot.totalSpots,
        floors: config.parkingLot.floors,
        zones: config.parkingLot.zones.join(', '),
        hourlyRate: config.pricing.hourlyRate,
        dailyRate: config.pricing.dailyRate,
        currency: config.pricing.currency,
        aiRecognition: config.features.aiRecognition,
        jetsonThor: config.features.jetsonThor,
        gemmaModel: config.features.gemmaModel,
        emailNotifications: config.notifications.email,
        smsNotifications: config.notifications.sms,
      });
      message.info('配置已重置');
    }
  };

  return (
    <div className="system-config">
      <Alert
        message="系統配置"
        description="修改系統配置後需要重啟服務才能生效，請謹慎操作。"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
      >
        {/* 停車場基本設置 */}
        <Card 
          title={
            <Space>
              <SettingOutlined />
              停車場基本設置
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="parkingLotName"
                label="停車場名稱"
                rules={[{ required: true, message: '請輸入停車場名稱' }]}
              >
                <Input placeholder="請輸入停車場名稱" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="totalSpots"
                label="總停車位數"
                rules={[{ required: true, message: '請輸入總停車位數' }]}
              >
                <InputNumber
                  min={1}
                  max={10000}
                  style={{ width: '100%' }}
                  placeholder="請輸入總停車位數"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="floors"
                label="樓層數"
                rules={[{ required: true, message: '請輸入樓層數' }]}
              >
                <InputNumber
                  min={1}
                  max={50}
                  style={{ width: '100%' }}
                  placeholder="請輸入樓層數"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="zones"
                label="停車區域"
                rules={[{ required: true, message: '請輸入停車區域' }]}
                extra="多個區域請用逗號分隔，例如：A, B, C, D"
              >
                <Input placeholder="例如：A, B, C, D" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 價格設置 */}
        <Card 
          title={
            <Space>
              <DollarOutlined />
              價格設置
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="hourlyRate"
                label="小時費率 (元)"
                rules={[{ required: true, message: '請輸入小時費率' }]}
              >
                <InputNumber
                  min={0}
                  max={1000}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="請輸入小時費率"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="dailyRate"
                label="日費率 (元)"
                rules={[{ required: true, message: '請輸入日費率' }]}
              >
                <InputNumber
                  min={0}
                  max={10000}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="請輸入日費率"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="currency"
                label="貨幣單位"
                rules={[{ required: true, message: '請輸入貨幣單位' }]}
              >
                <Input placeholder="例如：TWD, USD" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* AI功能設置 */}
        <Card 
          title={
            <Space>
              <RobotOutlined />
              AI功能設置
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="aiRecognition"
                label="AI車牌識別"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="啟用" 
                  unCheckedChildren="停用"
                />
              </Form.Item>
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                啟用後可自動識別車牌號碼
              </div>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="jetsonThor"
                label="Jetson Thor"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="啟用" 
                  unCheckedChildren="停用"
                />
              </Form.Item>
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                高性能AI推理加速
              </div>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="gemmaModel"
                label="Gemma模型"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="啟用" 
                  unCheckedChildren="停用"
                />
              </Form.Item>
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Google Gemma語言模型
              </div>
            </Col>
          </Row>

          {config?.features.aiRecognition && (
            <Alert
              message="AI功能狀態"
              description={
                <Space wrap>
                  <Tag color="green">車牌識別已啟用</Tag>
                  {config.features.jetsonThor && <Tag color="blue">Jetson Thor已啟用</Tag>}
                  {config.features.gemmaModel && <Tag color="purple">Gemma模型已啟用</Tag>}
                </Space>
              }
              type="success"
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        {/* 通知設置 */}
        <Card 
          title={
            <Space>
              <MailOutlined />
              通知設置
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="emailNotifications"
                label="郵件通知"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="啟用" 
                  unCheckedChildren="停用"
                />
              </Form.Item>
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                系統異常和重要事件郵件通知
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="smsNotifications"
                label="簡訊通知"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="啟用" 
                  unCheckedChildren="停用"
                />
              </Form.Item>
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                緊急情況簡訊通知
              </div>
            </Col>
          </Row>
        </Card>

        {/* 操作按鈕 */}
        <Card>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              size="large"
            >
              保存配置
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              size="large"
            >
              重置
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadConfig}
              loading={loading}
              size="large"
            >
              重新載入
            </Button>
          </Space>
        </Card>
      </Form>
    </div>
  );
};

export default SystemConfigComponent;